import React, { useState } from 'react';
import { Collapse, Drawer, styled } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Colors } from 'library';
import { Settings, ExpandMore, ExpandLess, Help, Mail, Phone } from '@mui/icons-material';

export type AppMode = 'recruit' | 'subs';

export const SIDEBAR_WIDTH = 220;

export interface RNavItem {
    label: string;
    icon: React.ReactNode;
    route: string;
}

interface ModeToggleConfig {
    mode: AppMode;
    onModeChange: (mode: AppMode) => void;
}

interface RLayoutSidebarProps {
    small: boolean;
    open: boolean;
    onClose: () => void;
    navItems: RNavItem[];
    bottomNavItems?: RNavItem[];
    settingsNavItems?: RNavItem[];
    modeToggle?: ModeToggleConfig;
    extraBottomContent?: React.ReactNode;
    showBottomSection?: boolean;
}

export function RLayoutSidebar({
    small,
    open,
    onClose,
    navItems,
    bottomNavItems,
    settingsNavItems,
    modeToggle,
    extraBottomContent,
    showBottomSection = true,
}: RLayoutSidebarProps) {
    const location = useLocation();
    const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/recruit/settings'));

    const isActive = (route: string) => {
        if (route === '/recruit' || route === '/home' || route === '/recruit-candidate') {
            return location.pathname === route;
        }
        return location.pathname.startsWith(route);
    };

    const handleNavClick = () => {
        if (small) onClose();
    };

    return (
        <StyledDrawer
            variant={small ? 'temporary' : 'persistent'}
            anchor="left"
            open={open}
            onClose={onClose}
            small={small}
        >
            <SidebarContent>
                <TopSection>
                    {modeToggle && (
                        <ModeToggle>
                            <ModeButton
                                active={modeToggle.mode === 'recruit'}
                                onClick={() => modeToggle.onModeChange('recruit')}
                            >
                                Recruit
                            </ModeButton>
                            <ModeButton
                                active={modeToggle.mode === 'subs'}
                                onClick={() => modeToggle.onModeChange('subs')}
                            >
                                Subs
                            </ModeButton>
                        </ModeToggle>
                    )}

                    <NavSection>
                        {navItems?.map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.route}
                                onClick={handleNavClick}
                                active={isActive(item.route)}
                            >
                                <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                            </NavLink>
                        ))}

                        {settingsNavItems && settingsNavItems.length > 0 && (
                            <>
                                <SettingsToggle onClick={() => setSettingsOpen(!settingsOpen)}>
                                    <NavIcon active={location.pathname.startsWith('/recruit/settings')}>
                                        <Settings />
                                    </NavIcon>
                                    <NavLabel active={location.pathname.startsWith('/recruit/settings')}>
                                        Settings
                                    </NavLabel>
                                    <ExpandIcon>
                                        {settingsOpen ? (
                                            <ExpandLess fontSize="small" />
                                        ) : (
                                            <ExpandMore fontSize="small" />
                                        )}
                                    </ExpandIcon>
                                </SettingsToggle>

                                <Collapse in={settingsOpen}>
                                    {settingsNavItems?.map((item) => (
                                        <NavLink
                                            key={item.label}
                                            to={item.route}
                                            onClick={handleNavClick}
                                            active={isActive(item.route)}
                                            nested
                                        >
                                            <NavIcon active={isActive(item.route)} nested>
                                                {item.icon}
                                            </NavIcon>
                                            <NavLabel active={isActive(item.route)} small>
                                                {item.label}
                                            </NavLabel>
                                        </NavLink>
                                    ))}
                                </Collapse>
                            </>
                        )}
                    </NavSection>

                    {bottomNavItems && bottomNavItems.length > 0 && (
                        <>
                            <Divider />
                            <NavSection>
                                {bottomNavItems?.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}
                            </NavSection>
                        </>
                    )}
                </TopSection>

                {showBottomSection && (
                    <BottomSection>
                        <Divider />
                        <NavSection>
                            <ExternalLink
                                href="https://chatwoot.join-tandem.com/hc/child-care-center-help-center/en"
                                target="_blank"
                            >
                                <NavIcon active={false}>
                                    <Help />
                                </NavIcon>
                                <NavLabel active={false}>FAQ</NavLabel>
                            </ExternalLink>
                        </NavSection>
                        {extraBottomContent}

                        <SupportBlock>
                            <SupportTitle>Need help?</SupportTitle>
                            <SupportRow>
                                <Mail sx={{ fontSize: 14, color: Colors.mediumGrey }} />
                                <SupportText>support@tandem.com</SupportText>
                            </SupportRow>
                            <SupportRow>
                                <Phone sx={{ fontSize: 14, color: Colors.mediumGrey }} />
                                <SupportText>1-800-TANDEM</SupportText>
                            </SupportRow>
                        </SupportBlock>
                    </BottomSection>
                )}
            </SidebarContent>
        </StyledDrawer>
    );
}

const StyledDrawer = styled(Drawer)<{ small: boolean }>(({ small }) => ({
    '& .MuiDrawer-paper': {
        width: SIDEBAR_WIDTH,
        backgroundColor: Colors.white,
        borderRight: `1px solid ${Colors.lightGrey}`,
        top: small ? 50 : 60,
        height: small ? 'calc(100vh - 50px)' : 'calc(100vh - 60px)',
        zIndex: 1200,
    },
}));

const SidebarContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    overflowY: 'auto',
});

const TopSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const BottomSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 8,
});

const ModeToggle = styled('div')({
    display: 'flex',
    margin: '16px 16px 8px',
    backgroundColor: Colors.lightGrey,
    borderRadius: 20,
    padding: 3,
});

const ModeButton = styled('button')<{ active: boolean }>(({ active }) => ({
    flex: 1,
    padding: '8px 0',
    border: 'none',
    borderRadius: 18,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    backgroundColor: active ? Colors.darkNavy : 'transparent',
    color: active ? Colors.white : Colors.darkNavy,
}));

const NavSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
});

const NavLink = styled(Link)<{ active: boolean; nested?: boolean }>(({ active, nested }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: nested ? '6px 8px 6px 24px' : '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: active ? Colors.lightGrey : 'transparent',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
}));

const SettingsToggle = styled('button')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
});

const NavIcon = styled('span')<{ active: boolean; nested?: boolean }>(({ active, nested }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginRight: 10,
    '& .MuiSvgIcon-root': {
        fontSize: nested ? 18 : 20,
        color: active ? Colors.darkNavy : Colors.mediumGrey,
    },
}));

const NavLabel = styled('span')<{ active: boolean; small?: boolean }>(({ active, small: isSmall }) => ({
    fontSize: isSmall ? 13 : 14,
    fontWeight: active ? 700 : 400,
    color: Colors.darkNavy,
}));

const ExpandIcon = styled('span')({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    color: Colors.mediumGrey,
});

const Divider = styled('div')({
    height: 1,
    backgroundColor: Colors.lightGrey,
    margin: '4px 16px',
});

const ExternalLink = styled('a')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
});

const SupportBlock = styled('div')({
    padding: '8px 16px',
});

const SupportTitle = styled('div')({
    fontSize: 12,
    fontWeight: 700,
    color: Colors.darkNavy,
    marginBottom: 4,
});

const SupportRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
});

const SupportText = styled('span')({
    fontSize: 12,
    color: Colors.mediumGrey,
});
